import type { QuizAnswers, QuizResult } from '@/types/quiz'
import { QUIZ_VERSION, SUBMIT_ENDPOINT } from './config'

export interface SubmitPayload {
  submission_id: string
  email: string | null
  answers: QuizAnswers
  /** Client-computed result - advisory only, server re-scores as canonical. */
  client_result: QuizResult
}

export interface SubmitResponseOk {
  ok: true
  submission_id: string
  result: QuizResult
}

export interface SubmitResponseErr {
  ok: false
  error: string
}

export type SubmitResponse = SubmitResponseOk | SubmitResponseErr

/**
 * POST to the submission endpoint. Same `submission_id` can be called twice
 * (once anonymous on result-screen mount, once with email on capture) - the
 * server upserts the record in place.
 *
 * Throws on network failure or non-ok response so callers can surface errors
 * in the UI. In dev (`npm run dev` without `netlify dev`) the endpoint 404s;
 * callers typically swallow that with a console log.
 */
export async function submitQuiz(payload: SubmitPayload): Promise<SubmitResponseOk> {
  const body = {
    ...payload,
    submitted_at: new Date().toISOString(),
    quiz_version: QUIZ_VERSION,
  }

  const res = await fetch(SUBMIT_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    // Try to pull a JSON error; fall back to status text.
    let message = `Submit failed (${res.status})`
    try {
      const data = (await res.json()) as Partial<SubmitResponseErr>
      if (data?.error) message = data.error
    } catch { /* noop */ }
    throw new Error(message)
  }

  const data = (await res.json()) as SubmitResponse
  if (!data.ok) throw new Error(data.error || 'Submit failed')
  return data
}

/** Browser UUID - crypto.randomUUID is standard in all modern browsers. */
export function generateSubmissionId(): string {
  return crypto.randomUUID()
}
